import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Logger service for controlled console logging
 * Only logs in dev, staging, and local environments
 * No logging in production for performance and security
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private readonly isLoggingEnabled: boolean;

  constructor() {
    // Enable logging if:
    // 1. debug flag is explicitly set to true in environment, OR
    // 2. production is false (dev/staging/local environments)
    // Disable logging if production is true and debug is not explicitly true
    this.isLoggingEnabled = environment.debug === true || 
                           (!environment.production && environment.debug !== false);
  }

  /**
   * Log informational messages
   * @param message - Message to log
   * @param optionalParams - Additional parameters to log
   */
  log(message?: any, ...optionalParams: any[]): void {
    if (this.isLoggingEnabled) {
      console.log(message, ...optionalParams);
    }
  }

  /**
   * Log warning messages
   * @param message - Warning message to log
   * @param optionalParams - Additional parameters to log
   */
  warn(message?: any, ...optionalParams: any[]): void {
    if (this.isLoggingEnabled) {
      console.warn(message, ...optionalParams);
    }
  }

  /**
   * Log error messages
   * Note: Errors might still be logged in production for critical issues
   * @param message - Error message to log
   * @param optionalParams - Additional parameters to log
   */
  error(message?: any, ...optionalParams: any[]): void {
    if (this.isLoggingEnabled) {
      console.error(message, ...optionalParams);
    }
  }

  /**
   * Log debug messages (most verbose)
   * @param message - Debug message to log
   * @param optionalParams - Additional parameters to log
   */
  debug(message?: any, ...optionalParams: any[]): void {
    if (this.isLoggingEnabled) {
      console.debug(message, ...optionalParams);
    }
  }

  /**
   * Log info messages (alias for log)
   * @param message - Info message to log
   * @param optionalParams - Additional parameters to log
   */
  info(message?: any, ...optionalParams: any[]): void {
    this.log(message, ...optionalParams);
  }

  /**
   * Check if logging is currently enabled
   * Useful for conditional logic that depends on logging state
   */
  isEnabled(): boolean {
    return this.isLoggingEnabled;
  }
}

